import React, { memo, useCallback, useMemo } from "react";
import PropTypes from 'prop-types';
import { Button, Col, Modal, Row } from "react-bootstrap";
import AdvanceTableWrapper from "components/common/advance-table/AdvanceTableWrapper";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import ExportButton from "components/common/advance-table/ExportButton";
import { rgbaColor } from 'helpers/utils';
import dayjs from "dayjs";
import conversationColumns from "./conversationColumns";
import RowDetailModal, { useRowDetail } from "./RowDetailModal";
import { pct, rankBy, userOf } from "./conversationStats";

// Count and share, with a tint sized against the largest row - a bar chart
// without the chart, so a breakdown reads at a glance in a dense column.
const BreakdownRow = ({ name, count, max, total, colour }) => (
    <div className="position-relative rounded overflow-hidden mb-1 px-2 py-1">
        <span
            className="position-absolute top-0 bottom-0 start-0 rounded"
            style={{ width: `${max > 0 ? (count / max) * 100 : 0}%`, backgroundColor: rgbaColor(colour, 0.18) }}
        />
        <div className="position-relative d-flex align-items-center fs--1">
            <span className="text-800 text-truncate me-auto pe-2" title={name}>{name}</span>
            <span className="text-900 fw-semi-bold">{count}</span>
            <span className="text-600 ms-2" style={{ minWidth: 46, textAlign: 'right' }}>
                {pct(count, total)}%
            </span>
        </div>
    </div>
);

BreakdownRow.propTypes = {
    name: PropTypes.string,
    count: PropTypes.number,
    max: PropTypes.number,
    total: PropTypes.number,
    colour: PropTypes.string
};

const Breakdown = ({ title, entries, total, colour, colours }) => {
    const max = entries.reduce((top, [, count]) => Math.max(top, count), 0);

    return (
        <Col xs={12} md={4}>
            <h6 className="text-700 fs--2 mb-2">{title}</h6>
            {entries.length === 0
                ? <p className="fs--1 text-600 mb-0">—</p>
                : entries.map(([name, count]) => (
                    <BreakdownRow
                        key={name}
                        name={name}
                        count={count}
                        max={max}
                        total={total}
                        colour={colours?.[name] ?? colour}
                    />
                ))}
        </Col>
    );
};

Breakdown.propTypes = {
    title: PropTypes.string,
    entries: PropTypes.array,
    total: PropTypes.number,
    colour: PropTypes.string,
    colours: PropTypes.object
};

const DrilldownModal = ({ drilldown, onHide }) => {
    const rows = drilldown?.rows ?? [];
    const { detail, openDetail, closeDetail } = useRowDetail();

    // Who and when, for the subset only - the page-level stats can't answer this
    // once the selection is narrowed to one rating, user or keyword.
    const meta = useMemo(() => {
        if (!drilldown) return [];

        const dates = rows
            .map(row => row.createdDate ? dayjs(row.createdDate) : null)
            .filter(date => date?.isValid())
            .sort((a, b) => a.valueOf() - b.valueOf());

        const users = rankBy(rows, userOf).length;
        const parts = [
            `${rows.length.toLocaleString()} of ${drilldown.total.toLocaleString()} questions · ${drilldown.share}%`,
            `${users.toLocaleString()} user${users === 1 ? '' : 's'}`
        ];

        if (dates.length) {
            const [first] = dates;
            const last = dates[dates.length - 1];
            parts.push(first.isSame(last, 'day')
                ? first.format('DD/MM/YYYY')
                : `${first.format('DD/MM/YYYY')} – ${last.format('DD/MM/YYYY')}`);
        }

        return parts;
    }, [drilldown, rows]);

    const tableProps = useMemo(() => ({
        bordered: true,
        striped: true,
        className: 'fs--1 mb-0',
        responsive: true
    }), []);

    // Never leave a detail stranded on screen if the drill-down behind it goes away.
    const hide = useCallback(() => {
        closeDetail();
        onHide();
    }, [closeDetail, onHide]);

    const drilldownView = (
        <Modal show={!!drilldown} onHide={hide} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <div>
                    <div className="fs--2 text-600 text-uppercase mb-1">{drilldown?.heading}</div>
                    <Modal.Title className="mb-1">{drilldown?.subject}</Modal.Title>
                    <div className="fs--1 text-600">{meta.join(' · ')}</div>
                </div>
            </Modal.Header>
            {drilldown && (
                <Modal.Body>
                    <Row className="g-3 mb-4">
                        {drilldown.breakdowns.map(dimension => (
                            <Breakdown key={dimension.title} {...dimension} total={rows.length} />
                        ))}
                    </Row>
                    <AdvanceTableWrapper
                        columns={conversationColumns}
                        data={rows}
                        sortable
                    >
                        <Row className="justify-content-between mb-3 mt-1">
                            <Col xs="auto">
                                <ExportButton table />
                            </Col>
                            <Col xs="auto">
                                <AdvanceTableSearchBox table placeholder="Search..." />
                            </Col>
                        </Row>
                        <div className="sticky-zone-stillages">
                            <AdvanceTable
                                table
                                headerClassName="bg-200 text-nowrap align-middle sticky-header-stillages"
                                rowClassName="align-middle white-space-nowrap cursor-pointer"
                                tableProps={tableProps}
                                handleClickRow={openDetail}
                            />
                        </div>
                    </AdvanceTableWrapper>
                </Modal.Body>
            )}
            <Modal.Footer>
                <Button variant="secondary" onClick={hide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );

    return (
        <>
            {drilldownView}
            {/* A sibling, not a child: it portals to the document body either way,
                and this keeps its lifetime independent of the drill-down's contents. */}
            <RowDetailModal detail={detail} onHide={closeDetail} />
        </>
    )
}

DrilldownModal.propTypes = {
    drilldown: PropTypes.shape({
        heading: PropTypes.string,
        subject: PropTypes.string,
        rows: PropTypes.array,
        total: PropTypes.number,
        share: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        breakdowns: PropTypes.array
    }),
    onHide: PropTypes.func
};

export default memo(DrilldownModal);
